import { IconDownload } from '@iwoca/icons';
import { fetchGetFormalOffers } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { useQuery, useMutation } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import styles from './RepaymentScheduleModal.module.css';
import RepaymentScheduleTable from './repaymentScheduleTable/RepaymentScheduleTable';
import { downloadAsAttachment } from '../../../../api/HelperAPI';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { normaliseRepaymentSchedule } from '../../MyOffers.helpers';
import { NormalisedRepaymentSchedule } from '../../MyOffers.types';

const RepaymentScheduleModal = ({
    offerId,
    drawdownAmount
}: {
    offerId: string;
    drawdownAmount: number;
}) => {
    const stateKey = useStateKey();

    const { data: formalOffer } = useQuery({
        queryKey: ['formal_offers', stateKey, offerId],
        queryFn: () => fetchGetFormalOffers({ stateKey }),
        select: (data) =>
            data.data?.formal_offers.find(
                (formal_offer) => formal_offer?.offer_id === offerId
            )
    });

    const productType = formalOffer ? formalOffer.product_type : null;

    const { data: drawdownSchedule } = useQuery({
        queryKey: [
            'draw_down_repayment_schedule',
            stateKey,
            drawdownAmount,
            productType,
            offerId
        ],
        queryFn: async () => {
            // eslint-disable-next-line iwoca/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/draw_down_repayment_schedule/${stateKey}/${productType}/${offerId}/?amount=${drawdownAmount}`
            );

            return response.json();
        },
        enabled: productType !== null
    });

    const { mutate: downloadSchedule, isLoading: isDownloading } = useMutation({
        mutationKey: [
            'download_draw_down_repayment_schedule',
            stateKey,
            drawdownAmount,
            productType,
            offerId
        ],
        mutationFn: async () => {
            // eslint-disable-next-line iwoca/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/draw_down_repayment_schedule/${stateKey}/${productType}/${offerId}/?amount=${drawdownAmount}`,
                {
                    headers: {
                        Accept: 'application/pdf'
                    }
                }
            );

            return downloadAsAttachment(response);
        }
    });

    if (formalOffer && drawdownSchedule && drawdownSchedule.data) {
        //todo: remove all this normalisation logic
        const repaymentSchedule: NonNullable<NormalisedRepaymentSchedule> =
            normaliseRepaymentSchedule({
                formalOffer,
                repaymentSchedule: drawdownSchedule.data
            })!;

        return (
            <div
                data-testid="repayments-schedule-modal"
                className={styles.RepaymentScheduleModal}
            >
                <div className="mt-xl flex flex-row items-baseline justify-between align-middle">
                    <div className={styles.title}>
                        {repaymentSchedule.duration ? (
                            <FormattedMessage
                                id="_.modal.loan_builder.repayment_schedule.title"
                                defaultMessage="Repayment schedule for {duration} months"
                                values={{
                                    duration: repaymentSchedule.duration
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="_.modal.loan_builder.repayment_schedule.example_title"
                                defaultMessage="Example Repayment schedule"
                            />
                        )}
                    </div>
                    {productType && offerId && (
                        <Button
                            onClick={() => downloadSchedule()}
                            loading={isDownloading}
                            variant="secondary-alt"
                        >
                            <FormattedMessage
                                id="_.modal.repayment_schedule.download_button"
                                defaultMessage="Download"
                            />
                            <IconDownload />
                        </Button>
                    )}
                </div>

                <RepaymentScheduleTable repaymentSchedule={repaymentSchedule} />
            </div>
        );
    } else return <MainContentPending />;
};

// eslint-disable-next-line import/no-default-export
export default RepaymentScheduleModal;
